import { apiClient } from './api-client';
import { starterFlowDsl } from '@/constants/starterFlowDsl';

export interface Flow {
  id: string;
  name: string;
  description?: string;
  tenantId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string;
  publishedVersionId?: string;
  deletedAt?: string | null;
  /** A starting point for new flows: copied, never published or shared. */
  isTemplate?: boolean;
  /** Templates only: how many flows "Use template" has started from it. */
  templateUseCount?: number;
  templateLastUsedAt?: string | null;
  versions?: FlowVersion[];
}

export interface FlowVersion {
  id: string;
  flowId: string;
  version: number;
  status: 'DRAFT' | 'IN_REVIEW' | 'PUBLISHED' | 'ARCHIVED';
  tenantId: string;
  dsl: any;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const flowsApi = {
  async getFlows() {
    const { data } = await apiClient.get<Flow[]>('/flows');
    return data;
  },

  async getFlow(id: string) {
    const { data } = await apiClient.get<Flow>(`/flows/${id}`);
    return data;
  },

  async createFlow(payload: { name: string; description?: string }) {
    const { data } = await apiClient.post<Flow>('/flows', payload);
    return data;
  },

  async getTemplates() {
    const { data } = await apiClient.get<Flow[]>('/flows', { params: { templates: 'true' } });
    return data;
  },

  /** A blank template with the same starter content as a new flow. */
  async createTemplate(payload: { name: string; description?: string }) {
    const { data } = await apiClient.post<Flow>('/flows', { ...payload, isTemplate: true });
    await flowsApi.createVersion(data.id, starterFlowDsl(data.id, data.name));
    return data;
  },

  /** Copy a flow into a new template. The flow itself is not changed. */
  async saveAsTemplate(id: string, payload: { name: string; description?: string }) {
    const { data } = await apiClient.post<Flow>(`/flows/${id}/save-as-template`, payload);
    return data;
  },

  /** Start a new, independent flow from a template. */
  async useTemplate(id: string, payload: { name: string; description?: string }) {
    const { data } = await apiClient.post<Flow>(`/flows/${id}/use-template`, payload);
    return data;
  },

  async updateFlow(id: string, payload: { name?: string; description?: string }) {
    const { data } = await apiClient.put<Flow>(`/flows/${id}`, payload);
    return data;
  },

  async deleteFlow(id: string) {
    const { data } = await apiClient.delete(`/flows/${id}`);
    return data;
  },

  async createVersion(flowId: string, dsl: any, comment?: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/versions`, { dsl, comment });
    return data;
  },

  async updateVersion(flowId: string, versionId: string, dsl: any) {
    const { data } = await apiClient.put(`/flows/${flowId}/versions/${versionId}`, { dsl });
    return data;
  },

  async lintVersion(flowId: string, versionId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/versions/${versionId}/lint`);
    return data;
  },

  async submitVersion(flowId: string, versionId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/versions/${versionId}/submit`);
    return data;
  },

  async approveVersion(flowId: string, versionId: string, approved: boolean, comment?: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/versions/${versionId}/approve`, { approved, comment });
    return data;
  },

  async publishFlow(flowId: string, versionId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/publish/${versionId}`);
    return data;
  },

  async unpublishFlow(flowId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/unpublish`);
    return data;
  },

  async getTrashedFlows() {
    const { data } = await apiClient.get<Flow[]>('/flows/trashed');
    return data;
  },

  async trashFlow(flowId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/trash`);
    return data;
  },

  async restoreFlow(flowId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/restore`);
    return data;
  },

  async permanentlyDeleteFlow(flowId: string) {
    const { data } = await apiClient.delete(`/flows/${flowId}`);
    return data;
  },

  async archiveFlow(flowId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/archive`);
    return data;
  },

  async restoreFromArchive(flowId: string) {
    const { data } = await apiClient.post(`/flows/${flowId}/restore-archive`);
    return data;
  },
};
